import React from 'react';
import ArrowLeft from 'svg/ArrowLeft';
import CloseIcon from 'svg/CloseIcon';
import Button, { ButtonLink, ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import type { RichText } from './props';

export type EventTypeLearnMoreModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: RichText;
  icon: string;
  iconClassName?: string;
  getStartedHref: string;
  onGoBack: () => void;
  /**
   * Set for VAIR-only event types: "Get started" calls this instead of linking,
   * so the VAIR check can route or explain what's missing.
   */
  onGetStarted?: () => void;
  /** VAIR role check still in flight — keeps the gated CTA from being clicked early. */
  isLoading?: boolean;
};

const EventTypeLearnMoreModal: React.FC<EventTypeLearnMoreModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  icon,
  iconClassName,
  getStartedHref,
  onGoBack,
  onGetStarted,
  isLoading,
}) => {
  return (
    <Modal isOpen={isOpen} handleClose={onClose} classNameMaxWidth="max-w-[62.5rem]">
      <div className="flex flex-row justify-end px-ds-3xl py-5">
        <ButtonText onClick={onGoBack}>
          <CloseIcon className="h-5 w-5" />
        </ButtonText>
      </div>
      <div className="mx-auto max-w-[39rem]">
        <div className="flex flex-col items-center px-ds-xl py-ds-3xl">
          <img
            src={icon}
            alt={title}
            className={iconClassName ?? 'mb-ds-2xl h-[5.5rem] w-auto object-contain'}
            aria-hidden
          />
          <h2 className="typography-product-heading-desktop max-sm:typography-product-heading-mobile mb-ds-xl text-center">
            {title}
          </h2>
          <p className="typography-product-body text-pretty text-color-text-lightmode-primary">
            {description.map((segment, i) =>
              segment.bold ? (
                <strong key={i}>{segment.text}</strong>
              ) : (
                <React.Fragment key={i}>{segment.text}</React.Fragment>
              ),
            )}
          </p>

          <div className="mb-[5rem] mt-ds-3xl flex w-full justify-center gap-ds-lg">
            <Button
              variant="secondary"
              size="lg"
              onClick={onGoBack}
              iconLeft={<ArrowLeft className="h-4 w-4" />}
              isInline
            >
              Go back
            </Button>
            {onGetStarted ? (
              <Button
                variant="primary"
                size="lg"
                onClick={onGetStarted}
                loading={isLoading}
                isInline
              >
                Get started
              </Button>
            ) : (
              <ButtonLink
                href={getStartedHref}
                variant="primary"
                size="lg"
                onClick={onClose}
                isInline
              >
                Get started
              </ButtonLink>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EventTypeLearnMoreModal;
